import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  ConfigProvider,
  App,
} from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import LoginImage from "../../assets/login_image.webp";
import SupportContact from "../../components/auth/SupportContact";
import { requestNewInvitation } from "../../api/invite_link_endpoints";
import { forgotPasswordSendEmail } from "../../api/marketOverviewApi";

const { Title, Paragraph, Text } = Typography;

/**
 * Branded landing for `/invitation-expired`. Users arrive here when they
 * click an activation link whose 30-day window has lapsed. They can request
 * a fresh link without contacting support; the underlying account is
 * preserved by the backend.
 *
 * If `?email=...` is present in the URL we pre-fill the form (the verify
 * route redirects with it). If not, the user types their email manually.
 *
 * The backend `POST /users/request-new-invitation` deliberately returns the
 * same 200 shape for unknown/activated emails (enumeration defence), so we
 * always transition to the `sent` state on success and let the user discover
 * via their inbox whether anything actually arrived.
 */
const InvitationExpiredPage = ({ mode = "invite" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | sent
  const [submittedEmail, setSubmittedEmail] = useState("");

  const isReset = mode === "reset";
  const linkLabel = isReset ? "reset" : "invitation";
  const prefilledEmail = searchParams.get("email") || "";

  const onFinish = async ({ email }) => {
    setLoading(true);
    try {
      // Reset links re-request via forgot-password; invitations via
      // request-new-invitation. Both are enumeration-safe 200s.
      await (isReset
        ? forgotPasswordSendEmail({ email })
        : requestNewInvitation({ email }));
      setSubmittedEmail(email);
      setStep("sent");
    } catch (error) {
      if (error?.status === 429) {
        message.error({
          content:
            "Too many requests for this email. Please wait a few minutes and try again.",
          duration: 4,
        });
      } else {
        message.error({
          content:
            error?.message ||
            "Couldn't request a new invitation. Please try again.",
          duration: 4,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error({
      content: "Please enter a valid email address.",
      duration: 3,
    });
  };

  const handleBackToLogin = () => navigate("/login");

  const HeaderLogo = () => (
    <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
      Dash
      <span className="text-[1.25rem] font-semibold tracking-normal">
        ONE
      </span>
    </div>
  );

  const SentState = () => (
    <div className="w-full flex flex-col gap-[40px]">
      <Title
        level={2}
        className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
      >
        Check your email
      </Title>
      <div className="bg-[#fafafa] border border-[#f5f5f5] rounded-[24px] px-[18px] py-[16px] flex flex-col gap-[10px]">
        <Paragraph className="!m-0 !text-[16px] !leading-[25px] !text-[rgba(0,0,0,0.85)] font-normal">
          If an account exists for{" "}
          <Text strong className="!text-[rgba(0,0,0,0.85)]">
            {submittedEmail}
          </Text>
          , a new {linkLabel} link is on its way.
        </Paragraph>
        <Paragraph className="!m-0 !text-[16px] !leading-[25px] !text-[rgba(0,0,0,0.85)] font-normal">
          Please check your inbox (and your spam/junk folder).
        </Paragraph>
        <Paragraph className="!m-0 !text-[14px] !leading-[22px] !text-[rgba(0,0,0,0.45)] font-normal">
          The new link will be valid for 30 days.
        </Paragraph>
      </div>
      <div className="flex flex-col gap-[16px]">
        <div className="w-full h-[36px]">
          <Button
            type="primary"
            block
            className="h-full !rounded-[24px] !text-[16px] !font-normal !leading-[24px]"
            onClick={handleBackToLogin}
          >
            Back to Log in
          </Button>
        </div>
        <SupportContact />
      </div>
    </div>
  );

  const FormState = () => (
    <div className="w-full flex flex-col">
      <div className="flex flex-col items-center gap-4 text-center mb-6">
        <div className="w-12 h-12 bg-[#F2F4F7] rounded-xl flex items-center justify-center">
          <MailOutlined className="text-[#475467] text-[1.5rem]" />
        </div>
        <div className="text-[24px] font-semibold text-[#101828]">
          Your {linkLabel} link has expired
        </div>
        <div className="text-[14px] font-normal text-[#475467]">
          {isReset ? "Reset" : "Invitation"} links are valid for 30 days.
          Request a new one and we'll email it to you — your account is
          unchanged.
        </div>
      </div>

      <ConfigProvider theme={{ token: { colorPrimary: "#0E49B5" } }}>
        <Form
          form={form}
          name="invitation-expired"
          initialValues={{ email: prefilledEmail }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
          className="w-full"
        >
          <Form.Item
            label={
              <span className="text-[14px] font-[500] text-[#344054] font-[Inter]">
                Email
              </span>
            }
            name="email"
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
            className="mb-4"
          >
            <Input
              placeholder="Enter your email"
              size="large"
              className="rounded-lg border-[#D0D5DD] py-3 px-[14px]"
              style={{
                fontSize: "16px",
                color: "black",
                fontFamily: "Inter",
              }}
            />
          </Form.Item>

          <Form.Item className="mb-4">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full border-none rounded-lg h-12 text-[16px] font-semibold"
              style={{ fontFamily: "Inter" }}
            >
              Request a new link
            </Button>
          </Form.Item>

          <Form.Item className="mb-4">
            <Button
              onClick={handleBackToLogin}
              size="large"
              className="w-full border-[#D0D5DD] rounded-lg h-12 text-[14px] font-medium text-[#344054]"
              style={{ fontFamily: "Inter" }}
            >
              ← Back to log in
            </Button>
          </Form.Item>

          <div className="text-center">
            <SupportContact />
          </div>
        </Form>
      </ConfigProvider>
    </div>
  );

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <HeaderLogo />
      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">
          {step === "sent" ? <SentState /> : <FormState />}
        </div>
      </div>
      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default InvitationExpiredPage;
